<script lang="ts">
  /**
   * The top of an options sheet: what the options are *about* — an avatar or
   * icon, a title, one line of detail — and the close button.
   */
  import type { Snippet } from 'svelte';

  import { shellText } from '../i18n.svelte.js';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    /** The avatar or icon, drawn in a round tile on the left. */
    readonly icon?: Snippet | undefined;
    readonly onclose: () => void;
    /** A test id for the title — a profile sheet's name. */
    readonly titleTestid?: string | undefined;
    /** The title's id, for the sheet's `aria-labelledby`. */
    readonly titleId?: string | undefined;
  }
  const { title, detail, icon, onclose, titleTestid, titleId }: Props = $props();
</script>

<div class="ui-options-header">
  {#if icon !== undefined}
    <span class="ui-options-avatar" aria-hidden="true">{@render icon()}</span>
  {/if}
  <span class="ui-options-who">
    <b class="ui-options-title" id={titleId} data-testid={titleTestid}>{title}</b>
    {#if detail !== undefined}
      <span class="ui-options-detail">{detail}</span>
    {/if}
  </span>
  <button
    type="button"
    class="ui-options-close"
    aria-label={shellText('close')}
    data-testid="ui-options-close"
    onclick={onclose}
  >
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  </button>
</div>

<style>
  .ui-options-header {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
    padding: var(--ui-space-1) 0 var(--ui-space-3);
    border-bottom: 1px solid var(--ui-line);
    margin-bottom: var(--ui-space-1);
  }

  .ui-options-avatar {
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font-weight: 800;
  }

  .ui-options-who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-options-title {
    font: var(--ui-text-heading);
    color: var(--ui-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-options-detail {
    font: var(--ui-text-caption);
    font-weight: 700;
    color: var(--ui-ink-2);
  }

  .ui-options-close {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: var(--ui-surface-2);
    color: var(--ui-ink);
  }

  .ui-options-close:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }
</style>
